import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { HERO_IMAGE } from "@/data/site";
import logoMark from "@/assets/nur-logo.png";

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollDown = () => {
    const el = document.getElementById("about");
    if (el && lenis) lenis.scrollTo(el, { duration: 1.6 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      {/* Static parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMAGE}
          alt="Golden sunlight glowing through tall forest trees at Epping Forest"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-[#14251b]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#14251b]/60" />

      {/* Centered logo / wordmark */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-10 text-[0.7rem] uppercase tracking-[0.4em] text-cream/80"
          data-testid="hero-overline"
        >
          Holistic Wellness · Epping Forest
        </motion.p>

        <motion.img
          src={logoMark}
          alt="Nur Therapy"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="w-[150px] sm:w-[190px] md:w-[230px] lg:w-[270px] drop-shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-8 max-w-2xl text-sm font-light leading-relaxed tracking-wide text-cream/85 md:text-base"
          data-testid="hero-tagline"
        >
          Holistic Healing · Acupuncture · Hijama Cupping · Vibrational Medicine
        </motion.p>

        <motion.button
          data-testid="hero-scroll-cue"
          onClick={scrollDown}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="group mt-14 flex flex-col items-center gap-3"
        >
          <span className="text-[0.65rem] uppercase tracking-[0.3em] text-cream/70 transition-colors group-hover:text-cream">
            See More...
          </span>
          <span className="relative flex h-12 w-px overflow-hidden bg-cream/30">
            <motion.span
              animate={{ y: ["-100%", "100%"] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-cream"
            />
          </span>
        </motion.button>
      </motion.div>
    </section>
  );
};
